import { useRef, useState, type FormEvent } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckIcon, CircleIcon, FileJsonIcon, Loader2Icon } from 'lucide-react';
import { CopyField } from '@/components/copy-field';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  CLIENT_STEP_LABELS,
  createClient,
  type ClientSetupError,
  type ClientStep,
  type CreatedClient,
} from '@/lib/api/clients';
import type { EntryInput } from '@/lib/api/types';
import { getAuthState } from '@/lib/auth-store';
import { buildClientNote, normaliseAddress, parseEntriesFile } from '@/lib/client-kit';

const INVITE_DAYS = 14;
const STEP_ORDER: ClientStep[] = ['organisation', 'site', 'licence', 'content', 'invite'];

interface FormState {
  clientName: string;
  ownerEmail: string;
  siteAddress: string;
  editorAddress: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const EMPTY: FormState = { clientName: '', ownerEmail: '', siteAddress: '', editorAddress: '' };

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.clientName.trim()) errors.clientName = 'Enter the client’s name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.ownerEmail.trim())) {
    errors.ownerEmail = 'Enter a valid email address.';
  }
  if (!normaliseAddress(form.siteAddress)) errors.siteAddress = 'Enter the website address.';
  if (form.editorAddress.trim() && !normaliseAddress(form.editorAddress)) {
    errors.editorAddress = 'Enter a web address, or leave this empty.';
  }
  return errors;
}

function NewClientPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<FormErrors>({});
  const [entries, setEntries] = useState<EntryInput[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [step, setStep] = useState<ClientStep | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const mutation = useMutation<CreatedClient, ClientSetupError, FormState>({
    mutationFn: (values) =>
      createClient(
        {
          clientName: values.clientName.trim(),
          ownerEmail: values.ownerEmail.trim().toLowerCase(),
          siteAddress: normaliseAddress(values.siteAddress) ?? values.siteAddress,
          editorAddress: normaliseAddress(values.editorAddress),
          entries,
          inviteExpiresInDays: INVITE_DAYS,
        },
        setStep,
      ),
    onSuccess: (created) =>
      toast.success(`${form.clientName.trim()} is ready`, { description: created.licenceNote }),
  });

  const update = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const readFile = async (file: File | undefined) => {
    setFileError(null);
    if (!file) return;
    const parsed = parseEntriesFile(await file.text());
    if (!parsed.entries) {
      setEntries(null);
      setFileName(null);
      setFileError(parsed.error ?? 'That file could not be read.');
      return;
    }
    setEntries(parsed.entries);
    setFileName(file.name);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    mutation.mutate(form);
  };

  if (mutation.isSuccess) {
    return <Result created={mutation.data} form={form} importedFrom={fileName} />;
  }

  const running = mutation.isPending;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <PageHeader
        eyebrow="Platform"
        title="New client"
        description="Give a client their own editor for a finished website. They get a private sign-in; you get a message to forward."
      />

      <form noValidate onSubmit={submit} className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>The client</CardTitle>
            <CardDescription>Who is this for, and where does their website live?</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field id="clientName" label="Client name" error={errors.clientName}>
              <Input
                id="clientName"
                value={form.clientName}
                onChange={update('clientName')}
                disabled={running}
                placeholder="PSSM DMV"
              />
            </Field>
            <Field
              id="ownerEmail"
              label="Their email"
              error={errors.ownerEmail}
              hint="They receive the sign-in link here."
            >
              <Input
                id="ownerEmail"
                type="email"
                value={form.ownerEmail}
                onChange={update('ownerEmail')}
                disabled={running}
                placeholder="president@example.org"
              />
            </Field>
            <Field id="siteAddress" label="Website address" error={errors.siteAddress}>
              <Input
                id="siteAddress"
                value={form.siteAddress}
                onChange={update('siteAddress')}
                disabled={running}
                placeholder="https://example.org"
              />
            </Field>
            <Field
              id="editorAddress"
              label="Editor address (optional)"
              error={errors.editorAddress}
              hint="Where they sign in, if not this dashboard. Used to build their link."
            >
              <Input
                id="editorAddress"
                value={form.editorAddress}
                onChange={update('editorAddress')}
                disabled={running}
                placeholder="https://example.org/admin"
              />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Current text and photos</CardTitle>
            <CardDescription>
              Run <code className="font-mono text-micro">siteflow instrument</code> on the site, then add
              the <code className="font-mono text-micro">siteflow.entries.json</code> it writes. The editor
              then opens showing exactly what the site says today. You can skip this and add it later.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              className="sr-only"
              aria-label="Starting content file"
              onChange={(event) => void readFile(event.target.files?.[0])}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                disabled={running}
                onClick={() => fileInput.current?.click()}
              >
                <FileJsonIcon /> {fileName ? 'Choose a different file' : 'Choose file'}
              </Button>
              {fileName && entries ? (
                <span className="text-data text-ink-muted">
                  {fileName}: {entries.length} items
                </span>
              ) : null}
            </div>
            {fileError ? (
              <p role="alert" className="text-data text-danger">
                {fileError}
              </p>
            ) : null}
          </CardContent>
        </Card>

        {mutation.isError ? <Failure error={mutation.error} /> : null}

        {running && step ? <Progress current={step} withContent={entries !== null} /> : null}

        <Button type="submit" size="lg" disabled={running} className="self-start">
          {running ? 'Setting up…' : 'Create client'}
        </Button>
      </form>
    </div>
  );
}

function Field(props: {
  id: string;
  label: string;
  error?: string | undefined;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={props.id}>{props.label}</Label>
      {props.children}
      {props.error ? (
        <p role="alert" className="text-micro text-danger">
          {props.error}
        </p>
      ) : props.hint ? (
        <p className="text-micro text-ink-subtle">{props.hint}</p>
      ) : null}
    </div>
  );
}

function Progress({ current, withContent }: { current: ClientStep; withContent: boolean }) {
  const steps = STEP_ORDER.filter((step) => withContent || step !== 'content');
  const at = steps.indexOf(current);
  return (
    <ol
      aria-live="polite"
      className="flex flex-col gap-2 rounded-lg border border-hairline bg-elevated p-4"
    >
      {steps.map((step, index) => (
        <li key={step} className="flex items-center gap-2 text-data text-ink">
          {index < at ? (
            <CheckIcon className="size-4 text-accent" />
          ) : index === at ? (
            <Loader2Icon className="size-4 animate-spin text-ink-muted" />
          ) : (
            <CircleIcon className="size-4 text-ink-subtle" />
          )}
          <span className={index > at ? 'text-ink-subtle' : undefined}>{CLIENT_STEP_LABELS[step]}</span>
        </li>
      ))}
    </ol>
  );
}

function Failure({ error }: { error: ClientSetupError }) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-1 rounded-lg border border-danger/40 bg-danger/5 p-4 text-data"
    >
      <p className="font-medium text-ink">
        Stopped while “{CLIENT_STEP_LABELS[error.step].toLowerCase()}”.
      </p>
      <p className="text-ink-muted">{error.reason}</p>
      {error.organisationId ? (
        <p className="text-ink-muted">
          The client was already created.{' '}
          <Link
            to="/admin/customers/$organisationId"
            params={{ organisationId: error.organisationId }}
            className="underline"
          >
            Open it
          </Link>{' '}
          to finish by hand rather than creating it again.
        </p>
      ) : null}
    </div>
  );
}

function Result({
  created,
  form,
  importedFrom,
}: {
  created: CreatedClient;
  form: FormState;
  importedFrom: string | null;
}) {
  const operator = getAuthState().user?.name ?? 'Your web team';
  const siteAddress = normaliseAddress(form.siteAddress) ?? form.siteAddress;
  const [note, setNote] = useState(() =>
    buildClientNote({
      clientName: form.clientName.trim(),
      siteAddress,
      setupLink: created.setupLink,
      operatorName: operator,
      expiresInDays: INVITE_DAYS,
    }),
  );
  const command = `npx siteflow instrument --site-key ${created.productionKey} --api-url ${created.apiUrl}`;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <PageHeader
        eyebrow="Platform"
        title={`${form.clientName.trim()} is set up`}
        description={created.licenceNote}
        actions={
          <Button asChild variant="secondary">
            <Link
              to="/admin/customers/$organisationId"
              params={{ organisationId: created.organisationId }}
            >
              Open organisation
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>1. Send them this</CardTitle>
          <CardDescription>
            The link works once and expires in {INVITE_DAYS} days. It is not shown again, so copy it now.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <CopyField label="Sign-in link" value={created.setupLink} />
          <Label htmlFor="note">Message to forward</Label>
          <Textarea id="note" rows={12} value={note} onChange={(event) => setNote(event.target.value)} />
          <Button
            type="button"
            variant="secondary"
            className="self-start"
            onClick={() =>
              void navigator.clipboard.writeText(note).then(() => toast.success('Message copied'))
            }
          >
            Copy message
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2. Connect the website</CardTitle>
          <CardDescription>
            {created.imported
              ? `Loaded ${created.imported.created} items from ${importedFrom ?? 'the file'} and published them. The site is already editable.`
              : 'Run this in the website’s folder. It marks up the text and photos and installs the script; nothing else changes.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {created.imported ? null : <CopyField label="Command" value={command} truncate={false} />}
          <CopyField label="Production key" value={created.productionKey} />
          {created.stagingKey ? <CopyField label="Staging key" value={created.stagingKey} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/admin/clients/new')({
  component: NewClientPage,
});
